import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormBuilder, FormControl, Validators } from '@angular/forms';
import { firstValueFrom } from 'rxjs';
import { Brand, Folder, Product } from 'src/typings';
import { ProductService } from '../product.service';
import { NotificationService } from 'src/app/services/notification.service';
import { extractApiErrorMessage } from 'src/app/shared/utils/extract-api-error-message.util';

@Component({
  selector: 'app-product-create',
  templateUrl: './create.component.html',
  styleUrls: ['./create.component.scss']
})
export class CreateComponent {
  @Output() createdEvent: EventEmitter<Product> = new EventEmitter();

  @Input() set initialArticle(value: string | null | undefined) {
    const normalized = value?.trim();
    if (!normalized) return;
    if (this.createForm.controls.article.value) return;
    this.createForm.controls.article.setValue(normalized);
  }

  @Input() set initialDescription(value: string | null | undefined) {
    const normalized = value?.trim();
    if (!normalized) return;
    if (this.createForm.controls.description.value) return;
    this.createForm.controls.description.setValue(normalized);
  }

  @Input() compact = false;
  @Input() submitLabel = 'Создать';
  @Output() loadingChange = new EventEmitter<boolean>();

  loading = false;

  constructor(
    private productService: ProductService,
    private fb: FormBuilder,
    private notificationService: NotificationService
  ) {}

  createForm = this.fb.group({
    description: ['', Validators.required],
    descriptionLong: new FormControl<string | undefined>(undefined),
    brand: this.fb.nonNullable.control<Brand | string | undefined>(undefined, Validators.required),
    article: new FormControl<string | undefined>(undefined, Validators.required),
    barcode: new FormControl<string | undefined>(undefined),
    folder: new FormControl<Folder | undefined>(undefined)
  });

  async create() {
    if (this.loading) return;
    const { description, descriptionLong, brand, article, barcode } = this.createForm.getRawValue();
    if (this.createForm.invalid || !description?.trim() || !article?.trim() ||
        (typeof brand === 'string' ? !brand.trim() : !brand?.id)) {
      this.createForm.markAllAsTouched();
      this.notificationService.warning('Укажите название, производителя и артикул товара');
      return;
    }
    this.loading = true;
    this.loadingChange.emit(true);
    this.createForm.disable({ emitEvent: false });
    try {
      const newProduct = await firstValueFrom(
        this.productService.create({
          description: description.trim(),
          description_long: descriptionLong || undefined,
          brand_id: typeof brand !== 'string' ? brand?.id : undefined,
          brand: typeof brand === 'string' ? brand.trim() : undefined,
          article: article.trim(),
          barcode: barcode || undefined
        })
      );
      this.createdEvent.emit(newProduct);
    } catch (err) {
      this.notificationService.showError(
        extractApiErrorMessage(err, 'Не удалось создать товар.')
      );
    } finally {
      this.createForm.enable({ emitEvent: false });
      this.loading = false;
      this.loadingChange.emit(false);
    }
  }
}
